import { DateRange } from "@mui/x-date-pickers-pro";
import { AutocompleteType } from "../../../common/types/autoComplete";
import { CustomerOrganization } from "../../customer/customerAPI";
import { parseAutocomplete } from "../../../common/helpers/filters";
import { SearchFilter } from "../../../common/components/functional/FilterSearchField/FilterSearchField";
import { format } from "date-fns";
import {
  isoFormat,
  leadingZerosDateFormat,
} from "../../../common/helpers/formatDateTime";
import i18n from "../../../i18n";
import { TFunction } from "i18next";
import { FILTER_QUEUE_OWN } from "../constants";

export const ORDER_QUICK_FILTER_LOCAL_STORAGE = "orderQuickFilter";

export type OrderFilterFormTypes = {
  queue: AutocompleteType[];
  dateRange: DateRange<Date>;
  orderHandler: AutocompleteType[];
  statuses: AutocompleteType[];
  workLocations: AutocompleteType[];
  types: AutocompleteType[];
  urgency: string;
  salesPersons: AutocompleteType[];
  hasJobAd: boolean | "";
  isBillable: boolean | "";
  timeOrderedRange: DateRange<Date>;
  customerOrg: CustomerOrganization[];
};

export const DEFAULT_ORDER_FORM: OrderFilterFormTypes = {
  queue: [],
  dateRange: [null, null],
  orderHandler: [],
  statuses: [],
  workLocations: [],
  types: [],
  urgency: "",
  salesPersons: [],
  hasJobAd: "",
  isBillable: "",
  timeOrderedRange: [null, null],
  customerOrg: [],
};

export const statusOpenValue = { id: "Open", name: i18n.t("order:Open") };
export const ownQueueValue = { id: "own", name: i18n.t("order:queueOwn") };

// Default filters: 'date from' current day, expert's own queues, automatching or no handler, and order is not filled
export const defaultOrderFilters: SearchFilter[] = [
  { name: "dateFrom", value: format(new Date(), isoFormat), type: "date" },
  { name: "dateTo", value: "", type: "date" },
  {
    name: "queue",
    value: [ownQueueValue.id],
    displayValue: ownQueueValue.name,
    lookupValues: [ownQueueValue],
  },
  {
    name: "orderHandler",
    value: ["autoHandler", "noHandler"],
    displayValue: `${i18n.t("order:automatching")}, ${i18n.t("order:noHandler")}`,
    lookupValues: [
      { id: "autoHandler", name: i18n.t("order:automatching") },
      { id: "noHandler", name: i18n.t("order:noHandler") },
    ],
  },
  { name: "statuses", value: [statusOpenValue.id] },
  { name: "workLocations", value: [], displayValue: "" },
  { name: "types", value: [] },
  { name: "automatching", value: "" },
  { name: "urgency", value: "" },
  { name: "salesPersons", value: [], displayValue: "" },
  { name: "hasJobAd", value: "" },
  { name: "isBillable", value: "" },
  { name: "timeOrderedFrom", value: "", type: "text" },
  { name: "timeOrderedTo", value: "", type: "text" },
  { name: "customerOrg", value: [], displayValue: "" },
];

export const OrderQuickFilterButton = {
  HANDLER_OWN_OPEN: "HANDLER_OWN_OPEN",
  SELLER_OWN_OPEN: "SELLER_OWN_OPEN",
  TEAM_ACTION_NEEDED: "TEAM_ACTION_NEEDED",
  TEAM_OPEN_SHORT: "TEAM_OPEN_SHORT",
  TEAM_OPEN_LONG: "TEAM_OPEN_LONG",
  EMPTY: "",
} as const;

export type OrderQuickFilterButtonType =
  (typeof OrderQuickFilterButton)[keyof typeof OrderQuickFilterButton];

export const getOrderQuickFilterPreset = (
  filterType: OrderQuickFilterButtonType,
  userId: string | undefined,
  t: TFunction,
): SearchFilter[] => {
  let presetValues: OrderFilterFormTypes;

  const queueFilterValue = [
    {
      id: FILTER_QUEUE_OWN.value,
      name: t(`order:${FILTER_QUEUE_OWN.translationKey}`),
    },
  ];
  const typeAllShiftsValue = { id: "AllShifts", name: t("order:AllShifts") };
  const typeFixedTermValue = { id: "FixedTerm", name: t("order:FixedTerm") };
  const typeContinuousValue = {
    id: "Continuous",
    name: t("order:Continuous"),
  };

  switch (filterType) {
    case OrderQuickFilterButton.HANDLER_OWN_OPEN:
      presetValues = {
        ...DEFAULT_ORDER_FORM,
        dateRange: [new Date(), null],
        orderHandler: [{ id: userId ?? "", name: t("common:me") }],
        statuses: [statusOpenValue],
      };
      break;
    case OrderQuickFilterButton.SELLER_OWN_OPEN:
      presetValues = {
        ...DEFAULT_ORDER_FORM,
        dateRange: [new Date(), null],
        salesPersons: [{ id: userId ?? "", name: t("common:me") }],
        statuses: [statusOpenValue],
      };
      break;
    case OrderQuickFilterButton.TEAM_ACTION_NEEDED:
      presetValues = {
        ...DEFAULT_ORDER_FORM,
        dateRange: [new Date(), null],
        queue: queueFilterValue,
        orderHandler: [{ id: "noHandler", name: t("order:noHandler") }],
        statuses: [statusOpenValue],
      };
      break;
    case OrderQuickFilterButton.TEAM_OPEN_SHORT:
      presetValues = {
        ...DEFAULT_ORDER_FORM,
        dateRange: [new Date(), null],
        queue: queueFilterValue,
        statuses: [statusOpenValue],
        types: [typeAllShiftsValue],
      };
      break;
    case OrderQuickFilterButton.TEAM_OPEN_LONG:
      presetValues = {
        ...DEFAULT_ORDER_FORM,
        dateRange: [new Date(), null],
        queue: queueFilterValue,
        statuses: [statusOpenValue],
        types: [typeFixedTermValue, typeContinuousValue],
      };
      break;
    default:
      presetValues = DEFAULT_ORDER_FORM;
  }

  const dateFromValue = presetValues.dateRange[0];
  const dateToValue = presetValues.dateRange[1];
  const timeOrderedFromValue = presetValues.timeOrderedRange[0];
  const timeOrderedToValue = presetValues.timeOrderedRange[1];

  return [
    {
      name: "queue",
      value: presetValues.queue.map((q) => q.id),
      displayValue: presetValues.queue.map((q) => q.name).join(", "),
      lookupValues: presetValues.queue,
    },
    {
      name: "dateFrom",
      value: dateFromValue ? format(dateFromValue, isoFormat) : "",
      displayValue: dateFromValue
        ? format(dateFromValue, leadingZerosDateFormat)
        : "",
    },
    {
      name: "dateTo",
      value: dateToValue ? format(dateToValue, isoFormat) : "",
      displayValue: dateToValue
        ? format(dateToValue, leadingZerosDateFormat)
        : "",
    },
    {
      name: "orderHandler",
      value: presetValues.orderHandler.map((oh) => oh.id),
      displayValue: presetValues.orderHandler.map((oh) => oh.name).join(", "),
      lookupValues: presetValues.orderHandler,
    },
    {
      name: "statuses",
      value: presetValues.statuses.map((s) => s.id),
      displayValue: presetValues.statuses.map((s) => s.name).join(", "),
      lookupValues: presetValues.statuses,
    },
    {
      name: "types",
      value: presetValues.types.map((t) => t.id),
      displayValue: presetValues.types.map((t) => t.name).join(", "),
      lookupValues: presetValues.types,
    },
    {
      name: "urgency",
      value: presetValues.urgency,
    },
    {
      name: "salesPersons",
      value: presetValues.salesPersons.map((sp) => sp.id),
      displayValue: presetValues.salesPersons.map((sp) => sp.name).join(", "),
      lookupValues: presetValues.salesPersons,
    },
    {
      name: "hasJobAd",
      value: presetValues.hasJobAd,
    },
    {
      name: "isBillable",
      value: presetValues.isBillable,
    },
    {
      name: "timeOrderedFrom",
      value: timeOrderedFromValue
        ? format(timeOrderedFromValue, isoFormat)
        : "",
      displayValue: timeOrderedFromValue
        ? format(timeOrderedFromValue, leadingZerosDateFormat)
        : "",
    },
    {
      name: "timeOrderedTo",
      value: timeOrderedToValue ? format(timeOrderedToValue, isoFormat) : "",
      displayValue: timeOrderedToValue
        ? format(timeOrderedToValue, leadingZerosDateFormat)
        : "",
    },
    {
      name: "customerOrg",
      value: presetValues.customerOrg,
      displayName: t("order:customerOrganization"),
    },
  ];
};

export const parseOrderFilters = (
  filters: SearchFilter[],
): OrderFilterFormTypes => {
  const find = (name: string) => filters.find((f) => f.name === name);

  const dateFromFilter = find("dateFrom");
  const dateToFilter = find("dateTo");
  const hasJobAdFilter = find("hasJobAd");
  const isBillableFilter = find("isBillable");
  const customerOrgFilter = find("customerOrg");

  return {
    queue: parseAutocomplete(find("queue")),
    dateRange: [
      dateFromFilter?.value ? new Date(dateFromFilter.value) : null,
      dateToFilter?.value ? new Date(dateToFilter.value) : null,
    ],
    orderHandler: parseAutocomplete(find("orderHandler")),
    statuses: parseAutocomplete(find("statuses")),
    workLocations: parseAutocomplete(find("workLocations")),
    types: parseAutocomplete(find("types")),
    urgency: find("urgency")?.value ?? "",
    salesPersons: parseAutocomplete(find("salesPersons")),
    hasJobAd:
      hasJobAdFilter?.value === "true"
        ? true
        : hasJobAdFilter?.value === "false"
          ? false
          : "",
    // Note: in UI this filter is named 'Perehdytysvuoro', so we want to find orders that are not billable.
    // When user selects 'yes' (true), we filter by isBillable = false, and vice versa.
    isBillable:
      isBillableFilter?.value === "true"
        ? false
        : isBillableFilter?.value === "false"
          ? true
          : "",
    timeOrderedRange: [
      find("timeOrderedFrom")?.value
        ? new Date(find("timeOrderedFrom")?.value)
        : null,
      find("timeOrderedTo")?.value
        ? new Date(find("timeOrderedTo")?.value)
        : null,
    ],
    customerOrg: parseAutocomplete(customerOrgFilter),
  };
};
